/**
 * AdminBrandingPage — tenant white-label branding editor (Administration →
 * Configuration → Branding). Reads/writes the existing tenant branding API
 * (GET/PUT /api/branding). Admin+ only.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Sparkles, Save, Loader2, RotateCcw, Copy, CheckCircle2, XCircle, Mail as MailIcon, TrendingUp, Download, Upload } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { applyBrandingToDOM, TERM_AREAS } from '@/lib/branding';
import { Monitor, LogIn } from 'lucide-react';
import ImportBrandingDialog from '@/components/branding/ImportBrandingDialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const DEFAULTS = {
  app_name: '', logo_url: '', favicon_url: '', app_icon_url: '',
  primary_color: '#3b82f6', accent_color: '#10b981', sidebar_bg: '#f8fafc',
  logo_url_dark: '', primary_color_dark: '', accent_color_dark: '', sidebar_bg_dark: '',
  cover_bg_color: '', cover_bg_image_url: '', login_slug: '', login_tagline: '', login_bg_image_url: '', root_page: '',
  email_reset_subject: '', email_reset_intro: '', email_invite_subject: '', email_invite_intro: '',
  invite_reminder_days: 2,
};

const MODE_DEFAULTS = {
  light: { primary_color: '#3b82f6', accent_color: '#10b981', sidebar_bg: '#f8fafc' },
  dark: { primary_color: '#60a5fa', accent_color: '#34d399', sidebar_bg: '#09090b' },
};

export default function AdminBrandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [branding, setBranding] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [iconUploading, setIconUploading] = useState(false);
  const [mode, setMode] = useState('light'); // which mode's logo+colors are being edited
  const [previewMode, setPreviewMode] = useState('light'); // preview compare toggle (independent)
  const [initial, setInitial] = useState(DEFAULTS); // last-saved/loaded snapshot for dirty check
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  // Branding bundle export / import (portable visual identity).
  const [exporting, setExporting] = useState(false);
  const [importBundle, setImportBundle] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [applyingBundle, setApplyingBundle] = useState(false);
  const [importThemesOnly, setImportThemesOnly] = useState(false);
  const importFileRef = React.useRef(null);
  const [slugStatus, setSlugStatus] = useState(null); // { checking, available, reason }
  const [sendingTest, setSendingTest] = useState(null); // 'reset' | 'invite' | null
  // AI branding generator (mirrors ThemeManager's AI palette+logo generation).
  const [aiMode, setAiMode] = useState('website'); // 'website' | 'text'
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);

  const aiGenerateBranding = async () => {
    if (!aiPrompt.trim()) { toast.error('Enter a description or website URL'); return; }
    setAiGenerating(true);
    try {
      const result = await api.aiGenerateTheme({ mode: aiMode, text: aiPrompt });
      const cols = result.colors || [];
      if (!cols.length) { toast.error('AI could not generate colors — try a different prompt'); return; }
      setBranding((p) => ({
        ...p,
        primary_color: cols[0] || p.primary_color,
        accent_color: cols[1] || cols[0] || p.accent_color,
        sidebar_bg: cols[2] || p.sidebar_bg,
        primary_color_dark: cols[3] || cols[0] || p.primary_color_dark,
        accent_color_dark: cols[4] || cols[1] || p.accent_color_dark,
        ...(result.logo_url ? { logo_url: result.logo_url } : {}),
        ...(result.name && !p.app_name ? { app_name: result.name } : {}),
      }));
      toast.success(result.logo_url ? 'AI branding applied — colors and logo set' : 'AI colors applied');
    } catch (err) { toast.error(err.message || 'AI generation failed'); }
    finally { setAiGenerating(false); }
  };

  const sendTest = async (kind) => {
    setSendingTest(kind);
    try {
      const r = await api.sendTestBrandingEmail(kind);
      toast.success(`Test ${kind} email sent to ${r.to}`);
    } catch (err) {
      toast.error(err.message || 'Could not send test email');
    } finally { setSendingTest(null); }
  };

  const releaseSlug = async (slug) => {
    try {
      const r = await api.releaseSlugHistory(slug);
      const hist = r.login_slug_history || [];
      setBranding((p) => ({ ...p, login_slug_history: hist }));
      setInitial((p) => ({ ...p, login_slug_history: hist }));
      toast.success(`Released "${slug}"`);
    } catch (err) {
      toast.error(err.message || 'Could not release URL');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const b = await api.getBranding();
      const merged = { ...DEFAULTS, ...(b || {}) };
      setBranding(merged);
      setInitial(merged);
    } catch (err) {
      toast.error(err.message || 'Could not load branding');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const isDirty = JSON.stringify(branding) !== JSON.stringify(initial);
  const slugBlocked = !!(slugStatus && !slugStatus.checking && !slugStatus.available);
  const onResetClick = () => { if (isDirty) setResetConfirmOpen(true); else load(); };

  const exportBranding = async () => {
    setExporting(true);
    try {
      const bundle = await api.exportBranding();
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const safe = (bundle.branding?.app_name || 'branding').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') || 'branding';
      const a = document.createElement('a');
      a.href = url;
      a.download = `${safe}-branding-bundle.json`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success('Branding bundle downloaded');
    } catch (err) {
      toast.error(err.message || 'Could not export branding');
    } finally { setExporting(false); }
  };

  const onImportFile = async (e) => {
    const file = e.target.files?.[0];
    if (importFileRef.current) importFileRef.current.value = '';
    if (!file) return;
    try {
      const text = await file.text();
      const bundle = JSON.parse(text);
      if (!bundle || bundle.format !== 'inzara.branding.bundle' || !bundle.branding) {
        toast.error('This file is not a branding bundle.');
        return;
      }
      setImportBundle(bundle);
      setImportThemesOnly(false);
      setImportOpen(true);
    } catch (err) {
      toast.error('That file could not be read as a branding bundle.');
    }
  };

  const applyImportedBundle = async () => {
    if (!importBundle) return;
    setApplyingBundle(true);
    try {
      await api.importBranding(importBundle, importThemesOnly);
      toast.success(importThemesOnly ? 'Themes added — your branding was kept' : 'Branding applied from bundle');
      setImportOpen(false);
      setImportBundle(null);
      await load();
      if (!importThemesOnly) {
        const fresh = await api.getBranding();
        applyBrandingToDOM({ ...DEFAULTS, ...(fresh || {}) });
      }
    } catch (err) {
      toast.error(err.message || 'Could not apply this bundle');
    } finally { setApplyingBundle(false); }
  };

  // Warn before leaving (refresh/close/external nav) with unsaved edits.
  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ''; return ''; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  const set = (k, v) => setBranding((p) => ({ ...p, [k]: v }));

  // Per-area feature-name overrides live under branding.terms[key] = {plural, singular}.
  const getTermVal = (key, form) => ((branding.terms || {})[key] || {})[form] || '';
  const setTerm = (key, form, value) => setBranding((p) => ({
    ...p,
    terms: { ...(p.terms || {}), [key]: { ...((p.terms || {})[key] || {}), [form]: value } },
  }));
  const resetTerm = (key) => setBranding((p) => {
    const next = { ...(p.terms || {}) };
    delete next[key];
    return { ...p, terms: next };
  });

  // Live slug availability — debounced check as the admin types.
  useEffect(() => {
    const slug = (branding.login_slug || '').trim();
    if (!slug) { setSlugStatus(null); return undefined; }
    if (slug === (initial.login_slug || '')) { setSlugStatus({ available: true, own: true }); return undefined; }
    setSlugStatus({ checking: true });
    const t = setTimeout(async () => {
      try {
        const r = await api.checkSlugAvailable(slug);
        setSlugStatus({ available: r.available, reason: r.reason });
      } catch {
        setSlugStatus(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [branding.login_slug, initial.login_slug]);

  const onFile = (k) => (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = () => set(k, r.result);
    r.readAsDataURL(f);
  };

  // App icon upload — stored in object storage (NOT base64) so it can be used
  // as the PWA install icon via a public URL. Mirrors the login-background flow.
  const onPickAppIcon = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    setIconUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const res = await api.uploadAppIcon(fd);
      const url = res?.url || res?.app_icon_url || '';
      const next = { ...branding, app_icon_url: url };
      setBranding(next);
      setInitial(next);
      applyBrandingToDOM(next);
      toast.success('App icon updated — it will be used when installing the app', {
        description: 'Want the browser tab icon to match too?',
        action: { label: 'Match favicon', onClick: () => setFaviconTo(url) },
      });
    } catch (err) {
      toast.error(err.message || 'Could not upload the app icon');
    } finally { setIconUploading(false); }
  };

  const clearAppIcon = async () => {
    try {
      const next = { ...branding, app_icon_url: '' };
      setBranding(next);
      await api.updateBranding({ app_icon_url: '' });
      setInitial(next);
      applyBrandingToDOM(next);
      toast.success('App icon removed');
    } catch (err) { toast.error(err.message || 'Could not remove the app icon'); }
  };

  // Make the browser favicon match the uploaded app icon (one URL powers both).
  const setFaviconTo = async (url) => {
    if (!url) return;
    const next = { ...branding, favicon_url: url };
    setBranding(next);
    setInitial(next);
    applyBrandingToDOM(next);
    try {
      await api.updateBranding({ favicon_url: url });
      toast.success('Favicon now matches the app icon');
    } catch (err) { toast.error(err.message || 'Could not set favicon'); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.updateBranding(branding);
      applyBrandingToDOM(branding);
      setInitial(branding);
      toast.success('Branding saved');
    } catch (err) { toast.error(err.message || 'Save failed'); }
    finally { setSaving(false); }
  };

  const renderColorRow = (label, k, fallback) => (
    <div key={k}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex gap-2 mt-1">
        <input type="color" value={branding[k] || fallback} onChange={(e) => set(k, e.target.value)} className="w-10 h-9 rounded cursor-pointer bg-transparent" data-testid={`branding-${k}-swatch`} />
        <Input value={branding[k] || ''} onChange={(e) => set(k, e.target.value)} placeholder={fallback} className="flex-1 text-foreground font-mono" data-testid={`branding-${k}`} />
      </div>
    </div>
  );

  const renderImageRow = (label, k, hint) => (
    <div key={k}>
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <div className="flex gap-2 mt-1 items-center">
        {branding[k] ? <img src={branding[k]} alt="" className="h-9 w-9 object-contain rounded border border-border/30 bg-muted/30" /> : null}
        <label className="px-3 py-1.5 rounded-lg border border-border/30 text-xs text-muted-foreground hover:text-foreground hover:bg-muted/30 cursor-pointer whitespace-nowrap">
          Choose File
          <input type="file" accept="image/*" className="hidden" onChange={onFile(k)} data-testid={`branding-${k}-file`} />
        </label>
        <Input value={branding[k] || ''} onChange={(e) => set(k, e.target.value)} placeholder={hint || 'or paste URL'} className="flex-1 text-foreground text-xs" data-testid={`branding-${k}`} />
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6" data-testid="admin-branding-page">
      <button onClick={() => navigate('/administration/configuration')} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-3" data-testid="branding-back">
        <ChevronLeft className="w-3.5 h-3.5" /> Administration
      </button>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-primary/10 flex items-center justify-center"><Sparkles className="w-5 h-5 text-primary" /></div>
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">Branding</h1>
          <p className="text-sm text-muted-foreground">White-label the app for your organization — logo, colors and themes.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Loading branding…</div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 glass-card rounded-2xl p-6 space-y-4 border border-border/40">
            <div><Label className="text-xs text-muted-foreground">App Name</Label><Input value={branding.app_name || ''} onChange={(e) => set('app_name', e.target.value)} placeholder="INZARA" className="mt-1 text-foreground" data-testid="branding-app_name" /></div>
            {renderImageRow('Favicon', 'favicon_url')}

            {/* App icon — used as the PWA install icon (Chrome "Install app").
                Stored in object storage (public URL) so browsers can fetch it. */}
            <div data-testid="branding-app-icon-row">
              <Label className="text-xs text-muted-foreground">App Icon</Label>
              <p className="text-[10px] text-muted-foreground mb-1">Used when installing the app (Chrome/Edge/Safari). Square PNG recommended (512×512).</p>
              <div className="flex gap-2 mt-1 items-center">
                {branding.app_icon_url ? (
                  <img src={branding.app_icon_url} alt="" className="h-10 w-10 object-contain rounded-lg border border-border/30 bg-muted/30" data-testid="branding-app-icon-preview" />
                ) : (
                  <div className="h-10 w-10 rounded-lg border border-dashed border-border/40 bg-muted/20" />
                )}
                <label className={`px-3 py-1.5 rounded-lg border border-border/30 text-xs whitespace-nowrap ${iconUploading ? 'opacity-60 cursor-wait' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30 cursor-pointer'}`}>
                  {iconUploading ? (<span className="flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Uploading…</span>) : 'Upload Icon'}
                  <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" disabled={iconUploading} onChange={onPickAppIcon} data-testid="branding-app-icon-file" />
                </label>
                {branding.app_icon_url && (
                  <button onClick={() => setFaviconTo(branding.app_icon_url)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="branding-app-icon-as-favicon">Use as favicon</button>
                )}
                {branding.app_icon_url && (
                  <button onClick={clearAppIcon} className="text-xs text-muted-foreground hover:text-destructive" data-testid="branding-app-icon-remove">Remove</button>
                )}
              </div>
            </div>

            {/* Feature names — white-label every sidebar/feature area */}
            <div className="pt-2 border-t border-border/40" data-testid="branding-feature-names">
              <Label className="text-xs text-muted-foreground">Feature names</Label>
              <p className="text-[10px] text-muted-foreground mb-2">Rename each area of the app. Plural is the area label (sidebar, headers); singular is one item ("New …", "Open …"). Blank uses the default.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {TERM_AREAS.map((a) => (
                  <div key={a.key} className="rounded-lg border border-border/30 p-2" data-testid={`term-row-${a.key}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-[11px] font-medium text-foreground">{a.plural}</span>
                      {((branding.terms || {})[a.key]) && (
                        <button onClick={() => resetTerm(a.key)} className="text-[9px] text-muted-foreground hover:text-foreground flex items-center gap-0.5" data-testid={`term-reset-${a.key}`}>
                          <RotateCcw className="w-2.5 h-2.5" /> Reset
                        </button>
                      )}
                    </div>
                    <Input value={getTermVal(a.key, 'plural')} onChange={(e) => setTerm(a.key, 'plural', e.target.value)} placeholder={a.plural} className="text-foreground text-xs h-7 mb-1" data-testid={`term-${a.key}-plural`} />
                    {a.singular ? (
                      <Input value={getTermVal(a.key, 'singular')} onChange={(e) => setTerm(a.key, 'singular', e.target.value)} placeholder={`one: ${a.singular}`} className="text-foreground text-xs h-7" data-testid={`term-${a.key}-singular`} />
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            {/* Branded login URL slug */}
            <div>
              <Label className="text-xs text-muted-foreground">Branded login URL</Label>
              <p className="text-[10px] text-muted-foreground mb-1">Give your team a direct, branded sign-in link. Letters, numbers and hyphens only.</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs text-muted-foreground font-mono truncate max-w-[45%]">{window.location.origin}/</span>
                <Input
                  value={branding.login_slug || ''}
                  onChange={(e) => set('login_slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  placeholder="cloverleaf"
                  className="flex-1 text-foreground font-mono"
                  data-testid="branding-login_slug"
                />
                <Button
                  type="button" variant="outline" size="sm" className="rounded-lg whitespace-nowrap"
                  disabled={!branding.login_slug}
                  data-testid="branding-login_slug-copy"
                  onClick={() => {
                    navigator.clipboard?.writeText(`${window.location.origin}/${branding.login_slug}`);
                    toast.success('Branded login URL copied');
                  }}
                >
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
              </div>
              {slugStatus && (
                <p className={`text-[10px] mt-1 flex items-center gap-1 ${slugStatus.checking ? 'text-muted-foreground' : slugStatus.available ? 'text-emerald-500' : 'text-destructive'}`} data-testid="branding-slug-status">
                  {slugStatus.checking ? (<><Loader2 className="w-3 h-3 animate-spin" /> Checking availability…</>)
                    : slugStatus.available ? (<><CheckCircle2 className="w-3 h-3" /> {slugStatus.own ? 'Your current URL' : 'Available'}</>)
                    : (<><XCircle className="w-3 h-3" /> {slugStatus.reason || 'Taken'}</>)}
                </p>
              )}
              {branding.login_slug && (
                <p className="text-[10px] text-muted-foreground mt-1 flex items-center gap-1" data-testid="branding-slug-signins">
                  <TrendingUp className="w-3 h-3 text-primary" />
                  <span className="text-foreground font-semibold">{(branding.slug_signins || {})[branding.login_slug] || 0}</span> sign-in(s) via this URL
                </p>
              )}
              {(branding.login_slug_history || []).length > 0 && (
                <div className="mt-2" data-testid="branding-slug-history">
                  <p className="text-[10px] text-muted-foreground mb-1">Old URLs (still redirect here):</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(branding.login_slug_history || []).map((s) => (
                      <span key={s} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted/40 border border-border/40 text-[10px] text-muted-foreground font-mono" data-testid={`slug-history-${s}`}>
                        /{s}
                        <span className="text-primary" title="sign-ins driven">· {(branding.slug_signins || {})[s] || 0}</span>
                        <button onClick={() => releaseSlug(s)} className="hover:text-destructive" title="Release this URL" data-testid={`release-slug-${s}`}>
                          <XCircle className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Branded developer-docs link */}
            <div data-testid="branding-devdocs-link">
              <Label className="text-xs text-muted-foreground">Developer docs link</Label>
              <p className="text-[10px] text-muted-foreground mb-1">
                Share this with your developers — the public API reference &amp; embedding guide,
                branded with your name{branding.login_slug ? '' : ' (set a branded login URL above to enable the brand)'}.
              </p>
              <div className="flex items-center gap-2 mt-1">
                <code className="flex-1 min-w-0 text-[11px] text-foreground font-mono break-all px-2 py-1.5 rounded-lg bg-muted/40 border border-border/40" data-testid="branding-devdocs-url" title={`${window.location.origin}/developer-docs${branding.login_slug ? `?brand=${branding.login_slug}` : ''}`}>
                  {`${window.location.origin}/developer-docs${branding.login_slug ? `?brand=${branding.login_slug}` : ''}`}
                </code>
                <Button
                  type="button" variant="outline" size="sm" className="rounded-lg whitespace-nowrap"
                  data-testid="branding-devdocs-copy"
                  onClick={() => {
                    const url = `${window.location.origin}/developer-docs${branding.login_slug ? `?brand=${branding.login_slug}` : ''}`;
                    navigator.clipboard?.writeText(url);
                    toast.success('Developer docs link copied');
                  }}
                >
                  <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                </Button>
                <Button
                  type="button" variant="ghost" size="sm" className="rounded-lg whitespace-nowrap"
                  data-testid="branding-devdocs-open"
                  onClick={() => window.open(`${window.location.origin}/developer-docs${branding.login_slug ? `?brand=${branding.login_slug}` : ''}`, '_blank', 'noopener')}
                >
                  Open
                </Button>
              </div>
            </div>


            {/* Branded login tagline */}
            <div>
              <Label className="text-xs text-muted-foreground">Login welcome message</Label>
              <p className="text-[10px] text-muted-foreground mb-1">A short tagline shown under your logo on the branded login page.</p>
              <Input
                value={branding.login_tagline || ''}
                onChange={(e) => set('login_tagline', e.target.value)}
                placeholder="e.g. Cloverleaf Analytics Portal"
                maxLength={120}
                className="mt-1 text-foreground"
                data-testid="branding-login_tagline"
              />
            </div>

            {/* Login background image (item 1) */}
            {renderImageRow('Login background image', 'login_bg_image_url')}

            {/* Live sign-in preview */}
            <div data-testid="login-preview">
              <Label className="text-xs text-muted-foreground">Sign-in preview</Label>
              <div className="mt-1 relative rounded-xl overflow-hidden border border-border/40 h-44 flex items-center justify-center bg-zinc-900">
                <div
                  className="absolute inset-0 opacity-30 bg-cover bg-center"
                  style={{ backgroundImage: `url(${branding.login_bg_image_url || 'https://images.unsplash.com/photo-1762430047160-db0e2670c533?w=1200&q=60'})` }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-black/30" />
                <div className="relative z-10 w-48 rounded-lg bg-zinc-950/80 border border-white/10 px-4 py-3 text-center backdrop-blur-sm">
                  {(branding.logo_url_dark || branding.logo_url) ? (
                    <img src={branding.logo_url_dark || branding.logo_url} alt="logo" className="h-6 mx-auto mb-1 object-contain" />
                  ) : (
                    <div className="text-white text-sm font-semibold mb-1">{branding.app_name || 'INZARA'}</div>
                  )}
                  {(branding.login_tagline) ? (
                    <div className="text-[8px] text-zinc-400 mb-2 truncate">{branding.login_tagline}</div>
                  ) : <div className="mb-2" />}
                  <div className="h-4 rounded bg-white/10 mb-1" />
                  <div className="h-4 rounded bg-white/10 mb-2" />
                  <div className="h-5 rounded text-[8px] text-white flex items-center justify-center font-medium" style={{ backgroundColor: branding.primary_color || '#6366f1' }}>Sign In</div>
                </div>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Updates live as you edit the logo, tagline, background, and primary color.</p>
            </div>

            {/* Per-tenant branded-link behaviour (item 2a): what the tenant's
                branded login link shows before sign-in. */}
            <div className="pt-2 border-t border-border/40" data-testid="branding-root-page-tenant">
              <Label className="text-xs text-muted-foreground">Branded link shows</Label>
              <p className="text-[10px] text-muted-foreground mb-2">
                What visitors to your branded link (<span className="font-mono">/{branding.login_slug || 'your-link'}</span>) see before signing in. Default is the sign-in page.
              </p>
              <div className="grid grid-cols-2 gap-2 max-w-md">
                <button
                  type="button"
                  onClick={() => set('root_page', 'login')}
                  className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-colors ${(branding.root_page || 'login') === 'login' ? 'border-primary bg-primary/10' : 'border-border/40 hover:border-border'}`}
                  data-testid="tenant-root-page-login"
                >
                  <LogIn className={`w-4 h-4 mt-0.5 ${(branding.root_page || 'login') === 'login' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span>
                    <span className="block text-xs font-medium text-foreground">Sign-in page</span>
                    <span className="block text-[10px] text-muted-foreground">Go straight to login</span>
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => set('root_page', 'landing')}
                  className={`flex items-start gap-2 rounded-lg border p-3 text-left transition-colors ${branding.root_page === 'landing' ? 'border-primary bg-primary/10' : 'border-border/40 hover:border-border'}`}
                  data-testid="tenant-root-page-landing"
                >
                  <Monitor className={`w-4 h-4 mt-0.5 ${branding.root_page === 'landing' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span>
                    <span className="block text-xs font-medium text-foreground">Landing page</span>
                    <span className="block text-[10px] text-muted-foreground">Show the marketing home page</span>
                  </span>
                </button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Save to apply.</p>
            </div>

            {/* Custom email copy */}
            <div className="pt-2 border-t border-border/40" data-testid="branding-email-copy">
              <Label className="text-xs text-muted-foreground">Email copy</Label>
              <p className="text-[10px] text-muted-foreground mb-2">Customize the subject and intro line of your branded emails. Leave blank to use the defaults.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-[10px] text-muted-foreground">Invite email subject</Label>
                  <Input value={branding.email_invite_subject || ''} onChange={(e) => set('email_invite_subject', e.target.value)} placeholder="You're invited to …" maxLength={140} className="mt-0.5 text-foreground text-xs" data-testid="branding-email_invite_subject" />
                  <Label className="text-[10px] text-muted-foreground mt-2 block">Invite email intro</Label>
                  <Input value={branding.email_invite_intro || ''} onChange={(e) => set('email_invite_intro', e.target.value)} placeholder="You've been invited to join …" maxLength={200} className="mt-0.5 text-foreground text-xs" data-testid="branding-email_invite_intro" />
                </div>
                <div>
                  <Label className="text-[10px] text-muted-foreground">Reset email subject</Label>
                  <Input value={branding.email_reset_subject || ''} onChange={(e) => set('email_reset_subject', e.target.value)} placeholder="Password Reset" maxLength={140} className="mt-0.5 text-foreground text-xs" data-testid="branding-email_reset_subject" />
                  <Label className="text-[10px] text-muted-foreground mt-2 block">Reset email intro</Label>
                  <Input value={branding.email_reset_intro || ''} onChange={(e) => set('email_reset_intro', e.target.value)} placeholder="We received a request to reset your password…" maxLength={200} className="mt-0.5 text-foreground text-xs" data-testid="branding-email_reset_intro" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Label className="text-[10px] text-muted-foreground">Send invite reminder</Label>
                <Input type="number" min={1} max={30}
                  value={branding.invite_reminder_days ?? 2}
                  onChange={(e) => set('invite_reminder_days', Math.max(1, Math.min(30, parseInt(e.target.value || '2', 10))))}
                  className="w-16 text-foreground text-xs h-7" data-testid="branding-invite_reminder_days" />
                <span className="text-[10px] text-muted-foreground">day(s) before a pending invite expires</span>
              </div>
            </div>


            {/* AI branding generator — colors + logo, mirroring theme AI */}
            <div className="rounded-xl border border-indigo-500/20 bg-indigo-500/5 p-3" data-testid="branding-ai-panel">
              <div className="flex items-center gap-2 mb-1.5">
                <Sparkles className="w-4 h-4 text-indigo-400" />
                <span className="text-xs font-semibold text-foreground">Generate with AI</span>
              </div>
              <p className="text-[10px] text-muted-foreground mb-2">Paste your website URL or describe your brand — AI sets your colors and logo, the same way it does for themes.</p>
              <div className="inline-flex rounded-lg border border-border/40 p-0.5 bg-muted/30 mb-2">
                {[['website', 'From Website'], ['text', 'Describe']].map(([k, l]) => (
                  <button key={k} onClick={() => setAiMode(k)} data-testid={`branding-ai-mode-${k}`}
                    className={`px-2.5 py-1 rounded-md text-[10px] transition-colors ${aiMode === k ? 'bg-indigo-500 text-white font-semibold' : 'text-muted-foreground hover:text-foreground'}`}>{l}</button>
                ))}
              </div>
              <div className="flex gap-2">
                <Input value={aiPrompt} onChange={(e) => setAiPrompt(e.target.value)} data-testid="branding-ai-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') aiGenerateBranding(); }}
                  placeholder={aiMode === 'website' ? 'e.g. stripe.com, your-company.com' : 'e.g. warm earth tones, trustworthy insurance brand'}
                  className="flex-1 text-foreground text-xs" />
                <Button onClick={aiGenerateBranding} disabled={aiGenerating} size="sm" data-testid="branding-ai-generate"
                  className="rounded-lg text-xs bg-indigo-500 text-white hover:bg-indigo-600 active:scale-95 whitespace-nowrap">
                  {aiGenerating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Generate
                </Button>
              </div>
            </div>

            {/* Light / Dark mode selector — logo + colors are set per mode */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs text-muted-foreground">Logo & Colors</Label>
                <div className="inline-flex rounded-lg border border-border/40 p-0.5 bg-muted/30">
                  {['light', 'dark'].map((m) => (
                    <button key={m} onClick={() => { setMode(m); setPreviewMode(m); }} data-testid={`branding-mode-${m}`}
                      className={`px-3 py-1 rounded-md text-xs capitalize transition-colors ${mode === m ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                      {m} mode
                    </button>
                  ))}
                </div>
              </div>
              {!branding.logo_url && !branding.logo_url_dark && (
                <div className="mb-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2" data-testid="branding-logo-prompt">
                  <Upload className="w-4 h-4 mt-0.5 shrink-0 text-amber-500" />
                  <p className="text-[11px] text-amber-600 dark:text-amber-400 leading-relaxed">
                    No logo uploaded yet — upload one below to show your own mark on the sidebar and
                    login screen{(branding.app_name && branding.app_name !== 'INZARA')
                      ? <> (they currently show your name <span className="font-semibold">“{branding.app_name}”</span> as text)</>
                      : ' (they currently show the default logo)'}.
                  </p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {mode === 'light' ? (
                  <>
                    {renderImageRow('Logo (light)', 'logo_url')}
                    <div />
                    {renderColorRow('Primary Color', 'primary_color', MODE_DEFAULTS.light.primary_color)}
                    {renderColorRow('Accent Color', 'accent_color', MODE_DEFAULTS.light.accent_color)}
                    {renderColorRow('Sidebar Background', 'sidebar_bg', MODE_DEFAULTS.light.sidebar_bg)}
                  </>
                ) : (
                  <>
                    {renderImageRow('Logo (dark)', 'logo_url_dark', 'defaults to light logo')}
                    <div />
                    {renderColorRow('Primary Color', 'primary_color_dark', MODE_DEFAULTS.dark.primary_color)}
                    {renderColorRow('Accent Color', 'accent_color_dark', MODE_DEFAULTS.dark.accent_color)}
                    {renderColorRow('Sidebar Background', 'sidebar_bg_dark', MODE_DEFAULTS.dark.sidebar_bg)}
                  </>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">Leave a dark value blank to reuse the light one.</p>
            </div>

            {/* Report PDF cover background */}
            <div className="pt-2 border-t border-border/40">
              <Label className="text-xs text-muted-foreground">Report cover background</Label>
              <p className="text-[10px] text-muted-foreground mb-2">Full-bleed color or image for the branded PDF cover page. An image overrides the color.</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {renderColorRow('Cover background color', 'cover_bg_color', '#0b1120')}
                {renderImageRow('Cover background image', 'cover_bg_image_url', 'optional — overrides color')}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-1">
              <Button onClick={save} disabled={saving || slugBlocked} className="rounded-lg" data-testid="branding-save">
                {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save Branding
              </Button>
              <Button variant="ghost" onClick={onResetClick} disabled={saving} className="rounded-lg text-muted-foreground" data-testid="branding-reset">
                <RotateCcw className="w-4 h-4 mr-1" /> Reset
              </Button>
              <div className="w-px h-6 bg-border/60 mx-1" />
              <Button variant="outline" onClick={exportBranding} disabled={exporting} className="rounded-lg" data-testid="branding-export">
                {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />} Export branding
              </Button>
              <Button variant="outline" onClick={() => importFileRef.current?.click()} className="rounded-lg" data-testid="branding-import">
                <Upload className="w-4 h-4 mr-1" /> Import branding
              </Button>
              <input ref={importFileRef} type="file" accept="application/json,.json" onChange={onImportFile} className="hidden" data-testid="branding-import-file" />
              {slugBlocked && (
                <span className="text-[10px] text-destructive flex items-center gap-1" data-testid="branding-save-blocked">
                  <XCircle className="w-3 h-3" /> Fix the branded URL before saving
                </span>
              )}
            </div>
          </div>

          {/* Live preview — compare light/dark independently of the edit tab */}
          <div className="space-y-4">
          <div className="glass-card rounded-2xl p-4 border border-border/40" data-testid="branding-preview">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Preview · {previewMode} mode</div>
              <div className="inline-flex rounded-lg border border-border/40 p-0.5 bg-muted/30">
                {['light', 'dark'].map((m) => (
                  <button key={m} onClick={() => setPreviewMode(m)} data-testid={`branding-preview-${m}`}
                    className={`px-2 py-0.5 rounded-md text-[10px] capitalize transition-colors ${previewMode === m ? 'bg-foreground text-background' : 'text-muted-foreground hover:text-foreground'}`}>
                    {m}
                  </button>
                ))}
              </div>
            </div>
            {(() => {
              const dark = previewMode === 'dark';
              const logo = (dark ? branding.logo_url_dark : branding.logo_url) || branding.logo_url;
              const primary = (dark ? branding.primary_color_dark : branding.primary_color) || MODE_DEFAULTS[previewMode].primary_color;
              const accent = (dark ? branding.accent_color_dark : branding.accent_color) || MODE_DEFAULTS[previewMode].accent_color;
              const sidebar = (dark ? branding.sidebar_bg_dark : branding.sidebar_bg) || MODE_DEFAULTS[previewMode].sidebar_bg;
              return (
                <div className="rounded-xl overflow-hidden border border-border/40">
                  <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: sidebar }}>
                    {logo ? <img src={logo} alt="" className="h-5 object-contain" /> : <div className="w-5 h-5 rounded" style={{ background: primary }} />}
                    <span className="text-sm font-semibold truncate" style={{ color: dark ? '#fff' : '#0f172a' }}>{branding.app_name || 'INZARA'}</span>
                  </div>
                  <div className="p-3 space-y-2" style={{ background: dark ? '#0b0b0e' : '#ffffff' }}>
                    <div className="h-2 rounded-full w-2/3" style={{ background: primary }} />
                    <div className="h-2 rounded-full w-1/2 opacity-70" style={{ background: accent }} />
                    <button className="mt-1 text-[11px] px-2.5 py-1 rounded-md text-white" style={{ background: primary }}>Primary button</button>
                  </div>
                </div>
              );
            })()}
            <p className="text-[10px] text-muted-foreground mt-2">Changes apply across the app after saving.</p>
          </div>

          {/* Branded password-reset email preview */}
          <div className="glass-card rounded-2xl p-4 border border-border/40" data-testid="branding-reset-email-preview">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1"><MailIcon className="w-3 h-3" /> Reset email preview</div>
            {(() => {
              const appName = branding.app_name || 'INZARA';
              const logo = branding.logo_url_dark || branding.logo_url || '';
              const primary = branding.primary_color || '#3b82f6';
              return (
                <div className="rounded-xl overflow-hidden border border-border/40" style={{ background: '#0a0a0b', padding: 16 }}>
                  {logo
                    ? <img src={logo} alt="" style={{ maxHeight: 28, marginBottom: 10 }} />
                    : <div style={{ color: primary, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{appName}</div>}
                  <div style={{ color: primary, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>Password Reset</div>
                  <p style={{ color: '#e2e8f0', fontSize: 10, margin: 0 }}>{branding.email_reset_intro || `We received a request to reset your password for your ${appName} account.`}</p>
                  <div style={{ margin: '12px 0' }}>
                    <span style={{ display: 'inline-block', padding: '6px 16px', background: primary, color: '#fff', borderRadius: 6, fontSize: 10, fontWeight: 600 }}>Reset Password</span>
                  </div>
                  <div style={{ borderTop: '1px solid #1e293b', margin: '10px 0 6px' }} />
                  <p style={{ color: '#64748b', fontSize: 9, margin: 0 }}>{appName}</p>
                </div>
              );
            })()}
            <p className="text-[10px] text-muted-foreground mt-2">Sent when a user requests a password reset. Save to apply.</p>
            <div className="flex items-center gap-2 mt-2">
              <Button type="button" variant="outline" size="sm" className="rounded-lg h-7 text-[11px]"
                disabled={sendingTest} data-testid="branding-test-reset-email"
                onClick={() => sendTest('reset')}>
                {sendingTest === 'reset' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MailIcon className="w-3 h-3 mr-1" />} Test reset email
              </Button>
              <Button type="button" variant="outline" size="sm" className="rounded-lg h-7 text-[11px]"
                disabled={sendingTest} data-testid="branding-test-invite-email"
                onClick={() => sendTest('invite')}>
                {sendingTest === 'invite' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <MailIcon className="w-3 h-3 mr-1" />} Test invite email
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground mt-1">Sends a sample to your own email using saved branding.</p>
          </div>

          {/* Branded invite email preview */}
          <div className="glass-card rounded-2xl p-4 border border-border/40" data-testid="branding-invite-email-preview">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1"><MailIcon className="w-3 h-3" /> Invite email preview</div>
            {(() => {
              const appName = branding.app_name || 'INZARA';
              const logo = branding.logo_url_dark || branding.logo_url || '';
              const primary = branding.primary_color || '#3b82f6';
              return (
                <div className="rounded-xl overflow-hidden border border-border/40" style={{ background: '#0a0a0b', padding: 16 }}>
                  {logo
                    ? <img src={logo} alt="" style={{ maxHeight: 28, marginBottom: 10 }} />
                    : <div style={{ color: primary, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{appName}</div>}
                  <div style={{ color: primary, fontWeight: 700, fontSize: 13, marginBottom: 6 }}>You're invited</div>
                  <p style={{ color: '#e2e8f0', fontSize: 10, margin: 0 }}>{branding.email_invite_intro || `You've been invited to join ${appName}.`}</p>
                  <div style={{ margin: '12px 0' }}>
                    <span style={{ display: 'inline-block', padding: '6px 16px', background: primary, color: '#fff', borderRadius: 6, fontSize: 10, fontWeight: 600 }}>Accept Invite</span>
                  </div>
                  <div style={{ borderTop: '1px solid #1e293b', margin: '10px 0 6px' }} />
                  <p style={{ color: '#64748b', fontSize: 9, margin: 0 }}>{appName}</p>
                </div>
              );
            })()}
            <p className="text-[10px] text-muted-foreground mt-2">Sent when you invite a teammate. Save to apply.</p>
          </div>
          </div>
        </div>
      )}

      <AlertDialog open={resetConfirmOpen} onOpenChange={setResetConfirmOpen}>
        <AlertDialogContent data-testid="branding-reset-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard unsaved branding changes?</AlertDialogTitle>
            <AlertDialogDescription>
              Your unsaved logo and color edits will be replaced with the last saved values. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="branding-reset-cancel">Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => { load(); setResetConfirmOpen(false); }} data-testid="branding-reset-discard">Discard changes</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <ImportBrandingDialog
        open={importOpen}
        onOpenChange={(v) => { setImportOpen(v); if (!v) setImportBundle(null); }}
        bundle={importBundle}
        currentAppName={initial.app_name}
        themesOnly={importThemesOnly}
        onThemesOnlyChange={setImportThemesOnly}
        onConfirm={applyImportedBundle}
        applying={applyingBundle}
      />
    </div>
  );
}
